// collection-contracts.ts — Sprint 10: typed contracts for the common generated-app
// record collections. Booking hard-coded `appointments` everywhere; this declares, ONCE,
// for each record type: who owns rows, who may read them, the required/optional fields, the
// status lifecycle, app-scoped uniqueness, and which actions each actor may perform. The
// generic owner route (app-owner-records.ts) and validateRecord() consume this.
//
// Pure, framework-free. It reuses CollectionRole (collection-roles.ts) and the lifecycle ids
// from status-lifecycle.ts. It does NOT persist anything and does NOT change runtime behavior.

import type { CollectionRole } from './collection-roles';

/** Who owns the ROWS of a collection (determines isolation + who the generic owner route serves). */
export type RecordOwner = 'customer' | 'owner' | 'app';

/** The END-USER read scope of a collection.
 *  - public:           everyone reads (owner-authored catalog/source).
 *  - customer-private: each customer reads only their OWN rows; the app OWNER reads ALL (owner route).
 *  - owner-only:       only the app owner reads/writes (no customer surface) — owner's private data.
 *  - device-local:     this-device-only, never server-backed (single-user self-data, honest today). */
export type RecordVisibility = 'public' | 'customer-private' | 'owner-only' | 'device-local';

export type CollectionFieldType = 'string' | 'number' | 'boolean' | 'object' | 'array' | 'iso-date';

export interface CollectionFieldSpec {
  name: string;
  type: CollectionFieldType;
  required: boolean;
  /** allowed values for an enum field (e.g. a status). */
  enum?: readonly string[];
}

export interface CollectionContract {
  collection: string;
  role: CollectionRole;
  owner: RecordOwner;
  visibility: RecordVisibility;
  requiredFields: readonly string[];
  optionalFields: readonly string[];
  fields: readonly CollectionFieldSpec[];
  /** the field that holds the lifecycle status, if any (e.g. 'status'). */
  statusField?: string;
  /** lifecycle id into status-lifecycle.ts LIFECYCLES (e.g. 'booking'). */
  lifecycle?: string;
  /** app-scoped uniqueness key (e.g. ['slot']) — DB-enforced (migration, deferred). */
  uniqueBy?: readonly string[];
  /** whether the app OWNER may mutate these rows via the generic owner route. */
  ownerWritable: boolean;
  creatorActions: readonly string[];
  customerActions: readonly string[];
  proofRequirements: readonly string[];
}

const STD_PROOFS = ['shape-validates', 'isolation', 'reload-persistence'] as const;

// The 15 common collections (Sprint 10 brief). Field lists are the minimal honest contract;
// a category may extend payloads, but these are the keys the platform validates + the owner
// route understands.
export const COLLECTION_CONTRACTS: Record<string, CollectionContract> = {
  appointments: {
    collection: 'appointments',
    role: 'transactional',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['serviceId', 'status'],
    // §PHASE11-STAFF — `assignedTo` is the row's half of staff scoping. A grant may carry a scope
    // (`assignedTo` ∈ {…}); a row without the field is UNASSIGNED and therefore outside every scoped
    // grant, which is the safe direction: an unlabelled appointment is invisible to a scoped
    // practitioner rather than visible to all of them.
    optionalFields: ['slot', 'slotDate', 'slotTime', 'startsAt', 'customerName', 'customerContact', 'notes', 'paymentStatus', 'assignedTo', 'arrivedAt'],
    fields: [
      { name: 'serviceId', type: 'string', required: true },
      { name: 'status', type: 'string', required: true, enum: ['requested', 'confirmed', 'arrived', 'completed', 'cancelled', 'no_show'] },
      { name: 'slot', type: 'string', required: false },
      { name: 'paymentStatus', type: 'string', required: false },
      { name: 'assignedTo', type: 'string', required: false },
    ],
    statusField: 'status',
    lifecycle: 'booking',
    uniqueBy: ['slot'],
    ownerWritable: true,
    creatorActions: ['confirm', 'cancel', 'complete', 'no_show', 'mark_paid'],
    customerActions: ['create', 'reschedule', 'cancel'],
    proofRequirements: [...STD_PROOFS, 'double-booking', 'owner-manage', 'status-reflection'],
  },
  // §SEP-2 F — BOOKING service catalog. The owner-authored list of bookable services (name / price /
  // duration / buffer / auto-vs-manual booking rule / media). Owner-owned + PUBLIC (a customer browses
  // the catalog to book, exactly like `products`), ownerWritable so the owner manages it via the generic
  // owner route (/v1/apps/:appId/owner/services) — no gateway code needed beyond this contract. No
  // status lifecycle: a service is a catalog entry (visibility/archive are plain fields), not a state
  // machine. `name` is the only required field so a legit create never 400s on an optional the form omits.
  services: {
    collection: 'services',
    role: 'source',
    owner: 'owner',
    visibility: 'public',
    requiredFields: ['name'],
    optionalFields: ['price', 'duration', 'bufferAfter', 'booking', 'description', 'mediaUrl', 'mediaAlt', 'hidden'],
    fields: [
      { name: 'name', type: 'string', required: true },
      { name: 'price', type: 'number', required: false },
      { name: 'duration', type: 'number', required: false },
    ],
    ownerWritable: true,
    creatorActions: ['create', 'update', 'archive'],
    customerActions: [],
    proofRequirements: ['shape-validates', 'owner-manage'],
  },
  // §SEP-2 F — BOOKING staff & resources. The owner's roster of people/rooms that fulfil services
  // (name / role / services-offered / note). Owner-owned + owner-only (internal roster, like
  // `employees` — no customer surface), ownerWritable so the owner manages it via /owner/resources.
  // No status lifecycle (visibility/archive are plain fields).
  resources: {
    collection: 'resources',
    role: 'owner-records',
    owner: 'owner',
    visibility: 'owner-only',
    requiredFields: ['name'],
    optionalFields: ['role', 'services', 'note', 'hidden'],
    fields: [
      { name: 'name', type: 'string', required: true },
      { name: 'services', type: 'array', required: false },
    ],
    ownerWritable: true,
    creatorActions: ['create', 'update', 'archive'],
    customerActions: [],
    proofRequirements: ['shape-validates', 'owner-manage'],
  },
  orders: {
    collection: 'orders',
    role: 'transactional',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['status', 'total'],
    optionalFields: ['items', 'pickupTime', 'paymentStatus', 'notes', 'assignedTo', 'trackingRef'],
    fields: [
      // The counter arm (accepted → preparing → ready) and the fulfilment arm (picking → packed →
      // shipped) share this enum because they share the record. Which arm a category's staff may
      // take is bound by STAFF_ACTIONS; two enums would drift and then disagree about one row.
      { name: 'status', type: 'string', required: true, enum: ['placed', 'accepted', 'preparing', 'ready', 'picking', 'packed', 'shipped', 'completed', 'cancelled'] },
      { name: 'total', type: 'number', required: true },
      { name: 'items', type: 'array', required: false },
      { name: 'paymentStatus', type: 'string', required: false },
      { name: 'assignedTo', type: 'string', required: false },
    ],
    statusField: 'status',
    lifecycle: 'order',
    ownerWritable: true,
    creatorActions: ['advance_status', 'cancel', 'mark_paid'],
    customerActions: ['create', 'cancel'],
    proofRequirements: [...STD_PROOFS, 'owner-manage', 'status-reflection'],
  },
  order_items: {
    collection: 'order_items',
    role: 'transactional',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['orderId', 'productId', 'quantity'],
    optionalFields: ['unitPrice', 'variant', 'notes'],
    fields: [
      { name: 'orderId', type: 'string', required: true },
      { name: 'productId', type: 'string', required: true },
      { name: 'quantity', type: 'number', required: true },
      { name: 'unitPrice', type: 'number', required: false },
    ],
    ownerWritable: false,
    creatorActions: [],
    customerActions: ['create'],
    proofRequirements: [...STD_PROOFS],
  },
  products: {
    collection: 'products',
    role: 'source',
    owner: 'owner',
    visibility: 'public',
    requiredFields: ['title', 'price'],
    optionalFields: ['images', 'category', 'variants', 'description', 'stock', 'badges'],
    fields: [
      { name: 'title', type: 'string', required: true },
      { name: 'price', type: 'number', required: true },
      { name: 'stock', type: 'number', required: false },
    ],
    ownerWritable: true,
    creatorActions: ['create', 'update', 'archive'],
    customerActions: [],
    proofRequirements: ['shape-validates', 'owner-manage'],
  },
  cart_items: {
    collection: 'cart_items',
    role: 'cart',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['productId', 'quantity'],
    optionalFields: ['variant', 'unitPrice'],
    fields: [
      { name: 'productId', type: 'string', required: true },
      { name: 'quantity', type: 'number', required: true },
    ],
    ownerWritable: false,
    creatorActions: [],
    customerActions: ['add', 'update', 'remove'],
    proofRequirements: ['shape-validates', 'isolation'],
  },
  rsvps: {
    collection: 'rsvps',
    role: 'transactional',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['eventId', 'status'],
    // §PHASE11-STAFF — check-in is a SEPARATE dimension from the RSVP status, deliberately. A guest
    // who answered "maybe" and then turned up is both maybe and present, and collapsing the two
    // would make the door person rewrite the guest's own answer in order to admit them.
    optionalFields: ['partySize', 'guestName', 'guestContact', 'checkedInAt', 'checkedInBy', 'walkUp'],
    fields: [
      { name: 'eventId', type: 'string', required: true },
      { name: 'status', type: 'string', required: true, enum: ['going', 'maybe', 'declined'] },
      { name: 'partySize', type: 'number', required: false },
    ],
    statusField: 'status',
    lifecycle: 'rsvp',
    uniqueBy: ['eventId'],
    ownerWritable: true,
    creatorActions: ['view', 'check_in'],
    customerActions: ['create', 'update'],
    proofRequirements: [...STD_PROOFS, 'owner-manage'],
  },
  tickets: {
    collection: 'tickets',
    role: 'transactional',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['subject', 'status'],
    optionalFields: ['body', 'priority', 'assignee', 'thread', 'escalatedAt', 'escalatedBy'],
    fields: [
      { name: 'subject', type: 'string', required: true },
      { name: 'status', type: 'string', required: true, enum: ['open', 'in_progress', 'resolved', 'closed', 'cancelled'] },
      { name: 'priority', type: 'string', required: false },
    ],
    statusField: 'status',
    lifecycle: 'ticket',
    ownerWritable: true,
    creatorActions: ['assign', 'reply', 'advance_status', 'cancel'],
    customerActions: ['create', 'reply', 'close'],
    proofRequirements: [...STD_PROOFS, 'owner-manage', 'status-reflection'],
  },
  contacts: {
    collection: 'contacts',
    role: 'owner-records',
    owner: 'owner',
    visibility: 'owner-only',
    requiredFields: ['name'],
    optionalFields: ['email', 'phone', 'company', 'tags', 'notes'],
    fields: [
      { name: 'name', type: 'string', required: true },
      { name: 'email', type: 'string', required: false },
    ],
    ownerWritable: true,
    creatorActions: ['create', 'update', 'archive'],
    customerActions: [],
    proofRequirements: ['shape-validates', 'owner-manage'],
  },
  deals: {
    collection: 'deals',
    role: 'owner-records',
    owner: 'owner',
    visibility: 'owner-only',
    requiredFields: ['title', 'stage'],
    optionalFields: ['value', 'contactId', 'closeDate', 'notes'],
    fields: [
      { name: 'title', type: 'string', required: true },
      { name: 'stage', type: 'string', required: true },
      { name: 'value', type: 'number', required: false },
    ],
    statusField: 'stage',
    lifecycle: 'deal', // TRACK C — folds into LIFECYCLES so PATCH { status } enforces valid-only stage moves
    ownerWritable: true,
    creatorActions: ['create', 'update', 'advance_stage', 'archive'],
    customerActions: [],
    proofRequirements: ['shape-validates', 'owner-manage'],
  },
  // TRACK C — the CRM activity timeline (append-only owner audit log). Owner-only business data; a
  // customer never reads or writes it. Authoritative appends go through the /owner/:collection/append
  // route (crm-activity-append.ts) which server-stamps actor + timestamp; the generic create is left
  // for shape-validated owner writes. In a composite the surface is namespaced `crm-activity` and
  // resolves to this base via baseCollectionName ('crm' ∈ COMPOSITE_SECONDARY_SLUGS).
  activity: {
    collection: 'activity',
    role: 'owner-records',
    owner: 'owner',
    visibility: 'owner-only',
    requiredFields: ['type', 'at'],
    optionalFields: ['actor', 'ref', 'from', 'to', 'note'],
    fields: [
      { name: 'type', type: 'string', required: true },
      { name: 'at', type: 'number', required: true },
      { name: 'actor', type: 'object', required: false },
      { name: 'ref', type: 'object', required: false },
    ],
    ownerWritable: true,
    creatorActions: ['create'], // append-only — no update/archive of an audit trail
    customerActions: [],
    proofRequirements: ['shape-validates', 'owner-manage'],
  },
  logs: {
    collection: 'logs',
    role: 'user-records',
    owner: 'customer',
    visibility: 'device-local',
    requiredFields: ['at'],
    optionalFields: ['value', 'metric', 'note'],
    fields: [
      { name: 'at', type: 'iso-date', required: true },
      { name: 'value', type: 'number', required: false },
    ],
    ownerWritable: false,
    creatorActions: [],
    customerActions: ['create', 'update', 'delete'],
    proofRequirements: ['shape-validates', 'reload-persistence'],
  },
  expenses: {
    collection: 'expenses',
    role: 'user-records',
    owner: 'customer',
    visibility: 'device-local',
    requiredFields: ['amount', 'at'],
    optionalFields: ['category', 'note', 'currency'],
    fields: [
      { name: 'amount', type: 'number', required: true },
      { name: 'at', type: 'iso-date', required: true },
      { name: 'category', type: 'string', required: false },
    ],
    ownerWritable: false,
    creatorActions: [],
    customerActions: ['create', 'update', 'delete'],
    proofRequirements: ['shape-validates', 'reload-persistence'],
  },
  documents: {
    collection: 'documents',
    role: 'user-records',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['title'],
    optionalFields: ['url', 'kind', 'size', 'at'],
    fields: [
      { name: 'title', type: 'string', required: true },
      { name: 'url', type: 'string', required: false },
    ],
    ownerWritable: false,
    creatorActions: [],
    customerActions: ['create', 'delete'],
    proofRequirements: ['shape-validates', 'isolation'],
  },
  assets: {
    collection: 'assets',
    role: 'user-records',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['kind'],
    optionalFields: ['url', 'prompt', 'title', 'at'],
    fields: [
      { name: 'kind', type: 'string', required: true },
      { name: 'url', type: 'string', required: false },
    ],
    ownerWritable: false,
    creatorActions: [],
    customerActions: ['create', 'delete'],
    proofRequirements: ['shape-validates', 'isolation'],
  },
  notifications: {
    collection: 'notifications',
    role: 'user-records',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['message', 'at'],
    optionalFields: ['read', 'kind', 'link'],
    fields: [
      { name: 'message', type: 'string', required: true },
      { name: 'at', type: 'iso-date', required: true },
      { name: 'read', type: 'boolean', required: false },
    ],
    ownerWritable: false,
    creatorActions: [],
    customerActions: ['mark_read'],
    proofRequirements: ['shape-validates', 'isolation'],
  },
  events: {
    collection: 'events',
    role: 'source',
    owner: 'owner',
    visibility: 'public',
    requiredFields: ['title', 'startsAt'],
    optionalFields: ['location', 'cover', 'description', 'capacity'],
    fields: [
      { name: 'title', type: 'string', required: true },
      { name: 'startsAt', type: 'iso-date', required: true },
      { name: 'capacity', type: 'number', required: false },
    ],
    ownerWritable: true,
    creatorActions: ['create', 'update', 'archive'],
    customerActions: [],
    proofRequirements: ['shape-validates', 'owner-manage'],
  },
  // §Batch 2d — waitlist. A SEPARATE collection (NOT a booking status): a customer joins when a
  // slot/event/window is full, the owner manages the queue (contacted / promoted_candidate /
  // dismissed). customer-private (each customer sees their own entry; the owner reads ALL via the
  // owner route). The customer may ONLY create with status 'new' (the CUSTOMER_WRITE_GUARDS entry
  // below clamps it); only the owner transitions it. No auto-promotion into a booking/order.
  waitlist: {
    collection: 'waitlist',
    role: 'transactional',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['status'],
    optionalFields: ['name', 'contact', 'requestedItem', 'slotDate', 'slotTime', 'note', 'createdAt'],
    fields: [
      { name: 'status', type: 'string', required: true, enum: ['new', 'contacted', 'promoted_candidate', 'dismissed'] },
      { name: 'name', type: 'string', required: false },
      { name: 'contact', type: 'string', required: false },
      { name: 'requestedItem', type: 'string', required: false },
    ],
    statusField: 'status',
    lifecycle: 'waitlist',
    ownerWritable: true,
    creatorActions: ['mark_contacted', 'promote_candidate', 'dismiss'],
    customerActions: ['create'],
    proofRequirements: [...STD_PROOFS, 'owner-manage', 'status-reflection'],
  },
  // §Phase 7 — Personal Tracker (server-backed, single-user PRIVATE records). Unlike the
  // device-local `logs`/`expenses`, these are `customer-private` (Supabase app_data + per-user RLS):
  // server-saved, survive a fresh browser/device, isolated per user. `ownerWritable: false` →
  // they are NOT in the owner-managed allowlist, so the creator/owner can NEVER read a user's
  // private tracker data (the key difference from Booking). No status lifecycle (fire-and-forget).
  tracker_logs: {
    collection: 'tracker_logs',
    role: 'user-records',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['at'],
    optionalFields: ['value', 'metric', 'unit', 'note', 'habitId', 'mood'],
    fields: [
      { name: 'at', type: 'iso-date', required: true },
      { name: 'value', type: 'number', required: false },
      { name: 'metric', type: 'string', required: false },
    ],
    ownerWritable: false,
    creatorActions: [],
    customerActions: ['create', 'update', 'delete'],
    proofRequirements: ['shape-validates', 'isolation', 'reload-persistence'],
  },
  habits: {
    collection: 'habits',
    role: 'user-records',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['name'],
    optionalFields: ['cadence', 'target', 'unit', 'color', 'archived', 'createdAt'],
    fields: [
      { name: 'name', type: 'string', required: true },
      { name: 'target', type: 'number', required: false },
      { name: 'archived', type: 'boolean', required: false },
    ],
    ownerWritable: false,
    creatorActions: [],
    customerActions: ['create', 'update', 'delete'],
    proofRequirements: ['shape-validates', 'isolation', 'reload-persistence'],
  },
  goals: {
    collection: 'goals',
    role: 'user-records',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['name'],
    optionalFields: ['target', 'progress', 'unit', 'dueAt', 'done', 'createdAt'],
    fields: [
      { name: 'name', type: 'string', required: true },
      { name: 'target', type: 'number', required: false },
      { name: 'progress', type: 'number', required: false },
      { name: 'done', type: 'boolean', required: false },
    ],
    ownerWritable: false,
    creatorActions: [],
    customerActions: ['create', 'update', 'delete'],
    proofRequirements: ['shape-validates', 'isolation', 'reload-persistence'],
  },
  // §Owner-console generalization — INVENTORY. The owner's stock catalog: each item is an owner-owned
  // row with a real availability lifecycle (in_stock ↔ low_stock ↔ out_of_stock → discontinued) the
  // owner transitions from the console, plus a tenant-scoped CSV export. Owner-only: a customer never
  // reads or mutates the stock ledger (isOwnerManagedCollection gates the owner route; there is no
  // customer surface). A row created without a status starts at the lifecycle initial (in_stock).
  /**
   * §A COLLECTION NAME THAT MEANT TWO THINGS. `items` below is the INVENTORY ledger —
   * owner-records, owner-only, sku / count / reorderPoint / status, lifecycle `inventory`. The
   * Collections category reused the same name for something else entirely: a personal catalogue of
   * {title, image, notes, tags} that the PERSON owns.
   *
   * It therefore inherited `owner: 'owner'` and `customerActions: []`, which made every customer
   * write on that lane a control a visitor could never successfully use. A previous fix removed the
   * add form rather than the collision, and the recipe was left displaying a catalogue with no way
   * to add to it — measured in production 2026-09-03: all three sealed Collections briefs (four
   * hundred vinyl records; a sneaker collection with purchase prices; sixty houseplants on a
   * watering schedule) render their content and offer a signed-in person no field anywhere.
   *
   * Same defect class as `product` in the routing table and `was` in the German token list: one
   * name meaning two things, and the wrong one winning.
   *
   * Safe to introduce as a new name: because `items` was owner-only on this lane, no customer has
   * ever been able to write a row to it, so there are none to orphan. The seeded catalogue is
   * re-registered here on every request from current engine code, so the 46 live collections apps
   * keep showing exactly what they show today.
   */
  collection_items: {
    collection: 'collection_items',
    role: 'user-records',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['title'],
    optionalFields: ['image', 'notes', 'tags', 'collection'],
    fields: [
      { name: 'title', type: 'string', required: true },
      { name: 'image', type: 'string', required: false },
      { name: 'notes', type: 'string', required: false },
      { name: 'collection', type: 'string', required: false },
    ],
    ownerWritable: true,
    creatorActions: ['create', 'update', 'delete'],
    customerActions: ['create', 'update', 'delete'],
    proofRequirements: [...STD_PROOFS],
  },
  items: {
    collection: 'items',
    role: 'owner-records',
    owner: 'owner',
    visibility: 'owner-only',
    requiredFields: ['name'],
    optionalFields: ['sku', 'category', 'count', 'unit', 'reorderPoint', 'location', 'status'],
    fields: [
      { name: 'name', type: 'string', required: true },
      { name: 'count', type: 'number', required: false },
      { name: 'reorderPoint', type: 'number', required: false },
      { name: 'status', type: 'string', required: false, enum: ['in_stock', 'low_stock', 'out_of_stock', 'discontinued'] },
    ],
    statusField: 'status',
    lifecycle: 'inventory',
    ownerWritable: true,
    creatorActions: ['create', 'update', 'adjust', 'archive'],
    customerActions: [],
    proofRequirements: ['shape-validates', 'owner-manage', 'status-reflection'],
  },
  // §Owner-console generalization — INVENTORY adjustments audit log. Owner-owned stock-movement rows
  // (item / delta / resulting / type / note). No status lifecycle — an adjustment is an immutable
  // ledger entry the owner lists + exports to CSV, never a state machine.
  adjustments: {
    collection: 'adjustments',
    role: 'owner-records',
    owner: 'owner',
    visibility: 'owner-only',
    requiredFields: ['item'],
    optionalFields: ['delta', 'resulting', 'type', 'note', 'at'],
    fields: [
      { name: 'item', type: 'string', required: true },
      { name: 'delta', type: 'number', required: false },
      { name: 'resulting', type: 'number', required: false },
    ],
    ownerWritable: true,
    creatorActions: ['create'],
    customerActions: [],
    proofRequirements: ['shape-validates', 'owner-manage'],
  },
  // §Owner-console generalization — QUIZ attempts. The end-user takes a self-test; each completed
  // attempt (quiz / score / total / pct) persists as their OWN customer-private row. ownerWritable so
  // the app owner (teacher) can list ALL attempts + export scores via the owner route — but each taker
  // still reads only their own rows (isolation). No status lifecycle: an attempt is read-only history,
  // never a state machine, so the console shows scores + CSV with no transition buttons. Honest scope:
  // this is a practice self-test (the answer key ships in-app), not a proctored/authoritative grade.
  attempts: {
    collection: 'attempts',
    role: 'user-records',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['quiz'],
    optionalFields: ['score', 'total', 'pct', 'passed', 'at'],
    fields: [
      { name: 'quiz', type: 'string', required: true },
      { name: 'score', type: 'number', required: false },
      { name: 'total', type: 'number', required: false },
      { name: 'pct', type: 'number', required: false },
    ],
    ownerWritable: true,
    creatorActions: ['view'],
    customerActions: ['create'],
    proofRequirements: ['shape-validates', 'isolation', 'owner-manage'],
  },
  // §Owner-console generalization — HR team roster. Owner-authored employee directory (name / role /
  // email / department). Owner-only, no lifecycle — the owner lists, manages, and exports the roster.
  // No fabricated colleagues: rows are exactly what the owner seeds/adds.
  employees: {
    collection: 'employees',
    role: 'owner-records',
    owner: 'owner',
    visibility: 'owner-only',
    requiredFields: ['name'],
    optionalFields: ['role', 'email', 'department', 'startDate', 'phone'],
    fields: [
      { name: 'name', type: 'string', required: true },
      { name: 'email', type: 'string', required: false },
    ],
    ownerWritable: true,
    creatorActions: ['create', 'update', 'archive'],
    customerActions: [],
    proofRequirements: ['shape-validates', 'owner-manage'],
  },
  // §Owner-console generalization — HR time-off requests. Owner-managed leave rows the owner approves
  // or denies via the `leave` lifecycle (requested → approved | denied, either party may cancel a
  // still-open request). Owner-owned (a single-owner HR tracker), owner-only visibility, CSV export.
  timeoff: {
    collection: 'timeoff',
    role: 'transactional',
    // §TIMEOFF-REQUEST — the same ownership model as orders / appointments / listing_submissions:
    // the record BELONGS to the person who submitted it, and they must be able to read it back to
    // see the manager's decision. It was 'owner'/'owner-only', which denied the requester a read of
    // their OWN request — so the recipe's "requests" screen, whose whole purpose is to reflect that
    // decision, would 403. The owner still sees every row via the owner route; the requester sees
    // only their own, through per-user RLS.
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['status'],
    optionalFields: ['type', 'start', 'end', 'days', 'note', 'employee'],
    fields: [
      { name: 'status', type: 'string', required: true, enum: ['requested', 'approved', 'denied', 'cancelled'] },
      { name: 'days', type: 'number', required: false },
    ],
    statusField: 'status',
    lifecycle: 'leave',
    ownerWritable: true,
    creatorActions: ['approve', 'deny', 'cancel'],
    // §TIMEOFF-REQUEST — the employee submits and may withdraw; the owner decides. Safe ONLY because
    // CUSTOMER_WRITE_GUARDS.timeoff clamps `status` to the requester-writable set and strips the
    // decision fields, so 'approved' is unreachable from the customer path.
    customerActions: ['create', 'cancel'],
    proofRequirements: [...STD_PROOFS, 'owner-manage', 'status-reflection'],
  },
  // §Owner-console generalization — DIRECTORY listings. Owner-authored/submitted local listings with a
  // real approval lifecycle (pending → approved | rejected → archived) the owner drives from the
  // console. Public visibility (end-users browse APPROVED listings — a pending/rejected one is withheld
  // by the recipe-side browse filter); ownerWritable so the owner approves + exports.
  listings: {
    collection: 'listings',
    role: 'source',
    owner: 'owner',
    visibility: 'public',
    requiredFields: ['title'],
    optionalFields: ['category', 'area', 'description', 'contact', 'status'],
    fields: [
      { name: 'title', type: 'string', required: true },
      { name: 'status', type: 'string', required: false, enum: ['pending', 'approved', 'rejected', 'archived'] },
    ],
    statusField: 'status',
    lifecycle: 'listing',
    ownerWritable: true,
    creatorActions: ['approve', 'reject', 'archive', 'update'],
    customerActions: [],
    proofRequirements: ['shape-validates', 'owner-manage', 'status-reflection'],
  },
  // §DEAD-CTRL-FIX (2026-07-16) — customer directory LISTING SUBMISSIONS. `listings` is owner-managed
  // (customer create → 403, a dead "submit a spot" form). A visitor instead submits to THIS customer-owned
  // collection: customer-private (the submitter sees ONLY their own; RLS-isolated), the owner reads ALL via
  // /owner/listing_submissions to review, and the owner APPROVE action PROMOTES the submission into a
  // SEPARATE owner-owned canonical `listings` record (the submission itself is never the public listing).
  // Customer create forces status→'pending' + strips owner-only fields (write-guard). Customer has NO
  // update/delete (status-conditional pending-only edit is not framework-enforceable → removed rather than
  // exposed as an over-permissive control). approve/reject/archive are OWNER-only via the promote endpoint.
  listing_submissions: {
    collection: 'listing_submissions',
    role: 'transactional',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['title', 'status'],
    // promotedListingId + provenance/timestamps are OWNER-set (stripped from any customer write).
    optionalFields: ['category', 'area', 'description', 'contact', 'promotedListingId', 'approvedAt', 'rejectedAt', 'rejectReason'],
    fields: [
      { name: 'title', type: 'string', required: true },
      { name: 'status', type: 'string', required: true, enum: ['pending', 'approved', 'rejected', 'archived'] },
      { name: 'category', type: 'string', required: false },
      { name: 'area', type: 'string', required: false },
      { name: 'promotedListingId', type: 'string', required: false },
    ],
    statusField: 'status',
    lifecycle: 'listing',
    ownerWritable: true,
    creatorActions: ['approve', 'reject', 'archive'],
    customerActions: ['create'],
    proofRequirements: [...STD_PROOFS, 'owner-manage', 'status-reflection'],
  },
  // §DEAD-CTRL-FIX — explicit CUSTOMER-OWNED PROGRESS collection (lesson-completion / saved-listing).
  // customer-private + ownerWritable:false → the customer manages ONLY their own rows (RLS) and the OWNER
  // has NO read route (isOwnerManagedCollection === ownerWritable) so personal progress stays private.
  // NEVER mutates the canonical lessons/listings. Upsert idempotently by (identity, srccol, ref): the
  // identity is the RLS row-owner; the client keys on (srccol, ref) → find-then-update, else create.
  progress: {
    collection: 'progress',
    role: 'transactional',
    owner: 'customer',
    visibility: 'customer-private',
    requiredFields: ['ref', 'srccol'],
    optionalFields: ['done'],
    fields: [
      { name: 'ref', type: 'string', required: true },
      { name: 'srccol', type: 'string', required: true },
      { name: 'done', type: 'boolean', required: false },
    ],
    uniqueBy: ['srccol', 'ref'],
    ownerWritable: false,
    creatorActions: [],
    customerActions: ['create', 'update', 'delete'],
    proofRequirements: [...STD_PROOFS],
  },
  // §B10 LOYALTY — a single-operator points/tier ledger. The owner awards points to a REAL customer
  // (customerId === contacts.id, the customer spine); each row is one award. ownerWritable so the
  // owner lists/manages every member's points via the owner route + CSV export. No fabricated
  // balances — every row is an award the owner actually made; the KPIs derive from real rows.
  points: {
    collection: 'points',
    role: 'owner-records',
    owner: 'owner',
    visibility: 'owner-only',
    requiredFields: ['customerId'],
    optionalFields: ['customerName', 'points', 'tier', 'at', 'note'],
    fields: [
      { name: 'customerId', type: 'string', required: true },
      { name: 'points', type: 'number', required: false },
    ],
    ownerWritable: true,
    creatorActions: ['create', 'update', 'archive'],
    customerActions: [],
    proofRequirements: ['shape-validates', 'owner-manage'],
  },
  // §B10 REVIEWS — a moderated ratings lane keyed by the REVIEWED entity id (reviewedId === a real
  // product/order on the product spine, or a directory listing on the listing spine; the FK-by-id
  // integrity built in B8). A review MUST tie to a COMPLETED transaction where that spine exists
  // (the recipe's reviewed picker only lists completed orders). Aggregation (count + average) is
  // SERVER-TRUTH: derived from the real rows, never a fabricated star average; honest empty state
  // when there are no reviews. ownerWritable so the owner can moderate/export; each row is a real
  // rating a customer left after a completed transaction.
  reviews: {
    collection: 'reviews',
    role: 'owner-records',
    owner: 'owner',
    visibility: 'owner-only',
    requiredFields: ['reviewedId'],
    optionalFields: ['reviewedLabel', 'rating', 'body', 'at', 'customerName'],
    fields: [
      { name: 'reviewedId', type: 'string', required: true },
      { name: 'rating', type: 'number', required: false },
    ],
    ownerWritable: true,
    creatorActions: ['create', 'archive'],
    customerActions: ['create'],
    proofRequirements: ['shape-validates', 'owner-manage'],
  },
  // §B10 two-role — COURSE catalog (owner-authored curriculum). In a single-user course app these are
  // the learner's own private courses; as a TWO-ROLE lane in an Education-OS composite the OWNER
  // manages the catalog (ownerWritable) while the student consumes it. No fabricated enrolments.
  courses: {
    collection: 'courses',
    role: 'owner-records',
    owner: 'owner',
    visibility: 'public',
    requiredFields: ['title'],
    optionalFields: ['level', 'description'],
    fields: [
      { name: 'title', type: 'string', required: true },
      { name: 'level', type: 'string', required: false },
    ],
    ownerWritable: true,
    creatorActions: ['create', 'update', 'archive'],
    customerActions: [],
    proofRequirements: ['shape-validates', 'owner-manage'],
  },
  // §B10 two-role — LESSONS under a course (owner-authored). The owner sequences/edits the lessons;
  // the student marks their own progress (the `done` toggle is the learner-side per-user field).
  lessons: {
    collection: 'lessons',
    role: 'owner-records',
    owner: 'owner',
    visibility: 'public',
    requiredFields: ['title'],
    optionalFields: ['course', 'order', 'content', 'done'],
    fields: [
      { name: 'title', type: 'string', required: true },
      { name: 'order', type: 'number', required: false },
    ],
    ownerWritable: true,
    creatorActions: ['create', 'update', 'archive'],
    customerActions: [],
    proofRequirements: ['shape-validates', 'owner-manage'],
  },
  // §B10 two-role — QUIZZES (owner-authored assessments). The owner builds/manages the quiz bank
  // (ownerWritable) while each learner takes them; a learner's scored `attempts` stay their own
  // customer-private rows (see the attempts contract). Honest scope: practice self-tests, not proctored.
  quizzes: {
    collection: 'quizzes',
    role: 'owner-records',
    owner: 'owner',
    visibility: 'public',
    requiredFields: ['title'],
    optionalFields: ['topic', 'questions'],
    fields: [
      { name: 'title', type: 'string', required: true },
      { name: 'topic', type: 'string', required: false },
    ],
    ownerWritable: true,
    creatorActions: ['create', 'update', 'archive'],
    customerActions: [],
    proofRequirements: ['shape-validates', 'owner-manage'],
  },
};

export const COLLECTION_NAMES = Object.keys(COLLECTION_CONTRACTS);

export function getCollectionContract(name: string): CollectionContract | null {
  return COLLECTION_CONTRACTS[name] ?? null;
}

/** §B3/B4 — the composable SECONDARY slugs the compositor namespaces as `<slug>-<name>`
 *  (compose-categories.ts namespaceRecipe). A namespaced owner collection like `crm-deals`
 *  is served by the owner route by resolving it back to its base contract (`deals`). Kept a
 *  bounded allowlist so an arbitrary hyphenated collection is NOT silently stripped. */
// §B11 — events + ticketing join the composite-secondary slugs so a namespaced secondary owner
// surface (`events-rsvps`, `ticketing-tickets`) resolves to its base owner-managed contract (`rsvps`,
// `tickets`) on the /owner/:col route — otherwise a demoted events/ticketing lane's owner surface
// would 400 (unsupported_collection) and its records would partial/leak. Additive: only names with a
// known `<slug>-` prefix are stripped, and no base contract carries such a prefix.
// §COMPOSE-ALLOWLIST (2026-07-27) — `booking` and `directory` became live composite-secondary lanes
// (select.ts BOOKING_HOST_PRIMARIES / DIRECTORY_HOST_PRIMARIES emit contract.secondary for them) but
// were never added here, so `booking-appointments` and `directory-listings` resolved to NO base
// contract: the /owner/:col route 400s on them and the booking double-book guard never runs. Verified
// safe — no base contract name starts with `booking`/`directory`, so nothing else re-resolves.
const COMPOSITE_SECONDARY_SLUGS: ReadonlySet<string> = new Set(['crm', 'inventory', 'loyalty', 'reviews', 'course', 'quizzes', 'events', 'ticketing', 'booking', 'directory']);

/** Resolve a (possibly namespaced) collection name to its BASE contract name: strip a known
 *  composite-secondary `<slug>-` prefix (so `crm-deals` → `deals`), otherwise return as-is. */
export function baseCollectionName(name: string): string {
  const dash = name.indexOf('-');
  if (dash > 0) {
    const slug = name.slice(0, dash);
    if (COMPOSITE_SECONDARY_SLUGS.has(slug)) {
      const rest = name.slice(dash + 1);
      if (COLLECTION_CONTRACTS[rest]) return rest;
    }
  }
  return name;
}

/** Owner-managed = the app owner may list/mutate these rows via the generic owner route.
 *  §B4 P2 fix — a namespaced secondary collection (`crm-deals`) resolves to its base
 *  contract (`deals`) before the allowlist lookup, so `/owner/crm-deals` is served instead
 *  of 400 unsupported_collection. */
export function isOwnerManagedCollection(name: string): boolean {
  return !!COLLECTION_CONTRACTS[baseCollectionName(name)]?.ownerWritable;
}

/** §OWNER-MGMT-SEP — CONTRACT-AWARE (never text): a customer create of this collection is FORBIDDEN by the
 *  server contract (the collection has a contract AND 'create' is not a customerAction — e.g. contacts /
 *  deals / products / items / points / employees / lessons / listings). A customer-creatable collection
 *  (reviews / listing_submissions / appointments / orders / rsvps / progress, or any uncontracted self-data)
 *  returns false, so its legitimate create form is NEVER flagged/hidden. Used by addForm to stamp
 *  data-owner-managed for the bridge's defense-in-depth hide (the server 403 remains the security boundary,
 *  the build strip remains authoritative). */
export function customerCreateForbidden(name: string): boolean {
  const c = COLLECTION_CONTRACTS[baseCollectionName(name)];
  return !!c && !c.customerActions.includes('create');
}

/** True when a collection is APPEND-ONLY: its only creator action is 'create' (e.g. the CRM `activity`
 *  audit log). Such rows are authoritative history — the generic owner UPDATE/DELETE routes must refuse
 *  to rewrite or remove them, so an audit trail can never be silently altered or destroyed. */
export function isAppendOnlyCollection(name: string): boolean {
  const c = COLLECTION_CONTRACTS[baseCollectionName(name)];
  return !!c && c.creatorActions.length > 0 && c.creatorActions.every((a) => a === 'create');
}

// ─── §CANONICAL-SEED-EDITABILITY ──────────────────────────────────────────────
//
// ONE answer to "may the CREATOR hand-edit the seed rows of this collection?".
//
// This used to be answered TWICE and the two answers drifted apart (launch-ledger R-8):
// surgical-diff.ts owned a hard-coded `USER_RECORD_COLLECTIONS` Set, while this file recorded
// `owner: 'customer'` per contract. 13 collections were customer-owned by contract yet absent from
// the Set — so a creator could seed-edit `listing_submissions`, `progress`, `order_items` and
// `attempts`, fabricating rows that read as another customer's activity — and `stories` was in the
// Set with no contract at all, so a contract-only reader mis-classified it as catalog.
// The Set now DERIVES from here, so the two cannot diverge again by construction.
//
// THE PRINCIPLE: a seed row is TEMPLATE content baked into the spec — what a brand-new user sees
// before they have any data of their own. So the question is not "who owns the row at runtime?" but
// "does a template row here IMPERSONATE another customer's action?"
//   • owner-owned catalog (services/products/listings/quizzes/…) — template rows ARE the product.
//   • customer transactions (appointments/orders/rsvps/tickets/listing_submissions/progress/…) —
//     a template row fabricates a transaction that never happened. Never editable. (§no-fake-data)
//   • personal self-data — split by the same test, see STARTER_CONTENT_COLLECTIONS below.

/** Collections that hold customer-written records but carry NO contract entry: AI-studio outputs
 *  and legacy aliases. Kept HERE, beside the contracts, so one module answers the question. */
export const UNCONTRACTED_CUSTOMER_RECORDS: ReadonlySet<string> = new Set([
  'cart', 'budget', 'submissions', 'messages', 'members', 'bookings', 'holds', 'creations', 'estimates',
  'receipts', 'palettes', 'stories', 'intake', 'briefs', 'workflows', 'insights', 'reports',
  'voiceworks', 'clips', 'voiceovers', 'loops', 'models3d',
]);

/** The `user-records` collections where a template row is legitimate ONBOARDING SCAFFOLDING rather
 *  than fabricated activity: it reads as "your starter habit", not "someone else did this". A habit
 *  tracker shipping three sample habits is normal and useful. The rest of `user-records` fails that
 *  test — a seeded `attempts` row is a quiz score nobody earned, a seeded `notifications` row is an
 *  event that never happened, and a seeded `documents`/`assets` row points at a file that does not
 *  exist. Those stay refused. */
const STARTER_CONTENT_COLLECTIONS: ReadonlySet<string> = new Set([
  'habits', 'goals', 'expenses', 'logs', 'tracker_logs',
]);

/** CANONICAL: may the creator's seed editor add/update/reorder/remove rows in this collection?
 *  Namespaced composite names resolve to their base contract first (`directory-listings`). */
export function isSeedEditableCollection(name: string): boolean {
  const base = baseCollectionName(name);
  // Engine-internal collections (the `__`-prefixed event/outbox spines) are never authored content.
  // Expressed as a RULE, not a list, so the names stay out of this file — tests/booking-events-
  // wiring-guard.test.mjs asserts they never appear here, because the generic owner route must not
  // be able to expose them.
  if (base.startsWith('__')) return false;
  if (UNCONTRACTED_CUSTOMER_RECORDS.has(base)) return false;
  const c = COLLECTION_CONTRACTS[base];
  // No contract and not a known customer-record alias ⇒ recipe-authored catalog content
  // (e.g. `albums`, `screams`). Editable, matching long-standing behaviour.
  if (!c) return true;
  if (c.owner === 'owner') return true;
  return STARTER_CONTENT_COLLECTIONS.has(base);
}

/** The collections the generic owner route will serve (allowlist). */
export function ownerManagedCollections(): string[] {
  return COLLECTION_NAMES.filter(isOwnerManagedCollection);
}

export interface RecordValidationResult {
  ok: boolean;
  errors: string[];
}

function typeOk(type: CollectionFieldType, v: unknown): boolean {
  switch (type) {
    case 'string':
    case 'iso-date':
      return typeof v === 'string';
    case 'number':
      return typeof v === 'number' && Number.isFinite(v);
    case 'boolean':
      return typeof v === 'boolean';
    case 'array':
      return Array.isArray(v);
    case 'object':
      return !!v && typeof v === 'object' && !Array.isArray(v);
    default:
      return false;
  }
}

/** Validate a record payload against its collection contract: required fields present,
 *  field types correct, enum (incl. status) values legal. Unknown collections fail closed. */
export function validateRecord(collection: string, payload: unknown): RecordValidationResult {
  const contract = getCollectionContract(collection);
  if (!contract) return { ok: false, errors: [`unknown_collection:${collection}`] };
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { ok: false, errors: ['payload_not_object'] };
  }
  const p = payload as Record<string, unknown>;
  const errors: string[] = [];
  for (const req of contract.requiredFields) {
    if (p[req] === undefined || p[req] === null || p[req] === '') errors.push(`missing_required:${req}`);
  }
  for (const f of contract.fields) {
    const v = p[f.name];
    if (v === undefined || v === null) {
      if (f.required) errors.push(`missing_required:${f.name}`);
      continue;
    }
    if (!typeOk(f.type, v)) errors.push(`bad_type:${f.name}`);
    if (f.enum && typeof v === 'string' && !f.enum.includes(v)) errors.push(`bad_enum:${f.name}:${v}`);
  }
  return { ok: errors.length === 0, errors };
}

// §audit BUG-SEC1 — CUSTOMER-WRITE OWNER-FIELD GUARD. The customer (end-user) data-plane write
// path (POST/PATCH /v1/apps/:appId/data/:collection) historically accepted the raw {data} blob, so
// a signed-in customer could forge OWNER-managed fields on their OWN transactional record — e.g.
// `paymentStatus:'marked_paid'` or an owner-only status like `completed`/`ready` on an order — and
// the owner dashboard (which trusts those fields) would display the forgery as truth. Confined to the
// attacker's own row (RLS holds; no money moves — Pomela never charges), but a payment-truth lie.
// The owner ROUTE is the source of truth for these fields (creatorActions); this guard enforces the
// same on the customer path: owner-only fields are forced to a safe default (create) or pinned to the
// stored value (update); `status` is clamped to the customer-writable set (initial + customer
// transitions). Legit customer writes (checkout `placed`, cancel `cancelled`, RSVP going/maybe/
// declined, ticket open/close + reply) are unchanged.
interface CustomerWriteGuard {
  // A field the owner manages. `read: 'owner'` also keeps it out of every customer read (PLAN step 12, D31): the field
  // classification (field-classification.ts) folds these marks in, so the one projection every read goes through hides
  // it. A mark without `read` is the owner's to write and the customer's to read (a payment status, a check-in).
  ownerOnlyFields: { name: string; createDefault?: unknown; read?: 'owner' }[];
  // PLAN step 13 (L2-PROBE-3-update) — fields the record keeps as created: a customer's update pins them to the stored
  // value (an order keeps the lines and the money it was placed with; a different order is a new order).
  keptAsCreated?: string[];
  customerStatuses?: string[]; // status values a customer may set; others are owner-only
  statusInitial?: string;
  // §BUG-TK2 — a message THREAD where each entry carries an author (`who`). The customer may only
  // author entries tagged `customerAuthor`; a non-customer ("support") entry is accepted ONLY if it
  // already existed in the previous record (i.e. the OWNER added it via the owner route, which does
  // not pass through this sanitizer). Otherwise it is RELABELED to the customer author — so a customer
  // cannot forge a "Support said X" bubble in their own (RLS-isolated) ticket.
  threadField?: string;
  customerAuthor?: string;
}
const CUSTOMER_WRITE_GUARDS: Record<string, CustomerWriteGuard> = {
  orders: { ownerOnlyFields: [{ name: 'paymentStatus', createDefault: 'awaiting_payment' }], customerStatuses: ['placed', 'cancelled'], statusInitial: 'placed', keptAsCreated: ['items', 'subtotal', 'taxRate', 'tax', 'shipping', 'total', 'itemCount'] },
  appointments: { ownerOnlyFields: [{ name: 'paymentStatus', createDefault: 'awaiting_payment' }], customerStatuses: ['requested', 'cancelled'], statusInitial: 'requested' },
  rsvps: { ownerOnlyFields: [{ name: 'checkedIn', createDefault: false }] }, // status (going/maybe/declined) is fully customer-owned
  tickets: { ownerOnlyFields: [{ name: 'assignee' }], customerStatuses: ['open', 'closed'], statusInitial: 'open', threadField: 'thread', customerAuthor: 'you' },
  // §Batch 2d — a customer may ONLY create a waitlist entry with status 'new'; the queue states
  // (contacted / promoted_candidate / dismissed) are OWNER-only, and promotedRef / ownerNote are
  // owner-managed (stripped from a customer write). So a customer can never forge a "promoted" /
  // "contacted" entry in their own row.
  waitlist: { ownerOnlyFields: [{ name: 'promotedRef' }, { name: 'ownerNote', read: 'owner' }], customerStatuses: ['new'], statusInitial: 'new' },
  // §DEAD-CTRL-FIX — a directory visitor may ONLY create a submission with status 'pending'; approve /
  // reject / archive are OWNER-only (lifecycle, owner-only transitions). So a submitter can never forge an
  // 'approved' spot, nor forge the owner-set promotion fields, into their own row — the owner approves it
  // in their console before it's featured (which promotes it into a separate canonical `listings` record).
  // §TIMEOFF-REQUEST — an employee SUBMITS a leave request; the owner DECIDES it. Structurally
  // identical to listing_submissions: the customer may only ever create status 'requested', and the
  // decision fields are owner-only, so an employee can never approve their own leave. Without this
  // guard entry, opening `customerActions` would have handed the requester `status` as well — which
  // is exactly why the 2026-08-24 classification kept the control unavailable.
  timeoff: { ownerOnlyFields: [{ name: 'approvedBy' }, { name: 'decidedAt' }, { name: 'decidedBy' }], customerStatuses: ['requested', 'cancelled'], statusInitial: 'requested' },
  listing_submissions: { ownerOnlyFields: [{ name: 'promotedListingId' }, { name: 'approvedAt' }, { name: 'rejectedAt' }, { name: 'rejectReason' }], customerStatuses: ['pending'], statusInitial: 'pending' },
};

/** PLAN step 12 (D31) — the owner-managed fields of `collection` that a customer may not read. */
export function ownerPrivateFields(collection: string): readonly string[] {
  return (CUSTOMER_WRITE_GUARDS[baseCollectionName(collection)]?.ownerOnlyFields ?? []).filter((f) => f.read === 'owner').map((f) => f.name);
}

/** PLAN step 12 (D31) — every field the platform's contract names for `collection` (its declared fields and the
 *  owner-managed ones), with the type the contract gives it. Empty for a collection the registry does not know. */
export function registryFields(collection: string): { name: string; type?: CollectionFieldType }[] {
  const base = baseCollectionName(collection);
  const c = COLLECTION_CONTRACTS[base];
  const guard = CUSTOMER_WRITE_GUARDS[base];
  const out = new Map<string, CollectionFieldType | undefined>();
  for (const f of c?.fields ?? []) out.set(f.name, f.type);
  for (const name of [...(c?.requiredFields ?? []), ...(c?.optionalFields ?? []), ...(guard?.ownerOnlyFields ?? []).map((f) => f.name)]) if (!out.has(name)) out.set(name, undefined);
  return [...out].map(([name, type]) => ({ name, ...(type ? { type } : {}) }));
}

export interface CustomerWriteSanitizeResult {
  data: Record<string, unknown>;
  stripped: string[]; // fields/status the guard corrected (for telemetry/tests)
}

// §EliteWeek-1C — UNIVERSAL owner-only / server-owned fields, enforced on EVERY customer write
// (not only the five guarded transactional collections). Live-proven forgery class: a customer
// POST {paymentStatus:'marked_paid'} on an rsvp/message PERSISTED as stored truth — inert for the
// payment lifecycle (those collections have none) but it surfaces in the owner's records/CSV
// export as if the owner had set it. Tenant/identity fields (appId / end-user ids / createdBy)
// are server-owned columns or server-derived — a client body must never smuggle look-alike copies
// into `data` where a generic surface could display them as authoritative.
// create → strip (or the guard's createDefault); update → pin to the stored row, else strip.
const UNIVERSAL_OWNER_ONLY_FIELDS = ['paymentStatus', 'appId', 'app_id', 'end_user_id', 'endUserId', 'ownerUserId', 'createdBy'] as const;

/** True when a customer write body carries a universal owner-only/server-owned field — the
 *  gateway uses this to read the stored row before an UPDATE so the guard can PIN (never
 *  elevate) the field. Costless for normal bodies that don't carry these fields. */
export function dataCarriesOwnerOnlyField(data: unknown): boolean {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  return UNIVERSAL_OWNER_ONLY_FIELDS.some((f) => (data as Record<string, unknown>)[f] !== undefined);
}

/** True when a collection has owner-managed fields/status a customer write must not forge — the
 *  gateway reads the stored row before a customer UPDATE only for these (skips the cost otherwise). */
export function isCustomerWriteGuarded(collection: string): boolean {
  // §NAMESPACED-GUARD-BYPASS (2026-07-27) — resolve the BASE contract first. authz.ts:66 already
  // authorizes a namespaced composite collection via baseCollectionName, so keying the GUARD on the
  // raw name meant `ticketing-tickets` was allowed as `tickets` and then sanitized as nothing:
  // a customer could set status/assignee and forge a support reply. Same asymmetry the owner path
  // already fixed at app-data.ts:733.
  return Object.prototype.hasOwnProperty.call(CUSTOMER_WRITE_GUARDS, baseCollectionName(collection));
}

/** Sanitize a CUSTOMER (end-user) write so it cannot forge owner-managed fields/status on a
 *  transactional record. `mode:'create'` forces owner-only fields to their safe default; `'update'`
 *  pins them to `prev` (the stored row). The UNIVERSAL owner-only fields (paymentStatus +
 *  tenant/identity fields) are enforced on EVERY collection. */
export function sanitizeCustomerWrite(
  collection: string,
  data: Record<string, unknown>,
  mode: 'create' | 'update',
  prev?: Record<string, unknown> | null,
): CustomerWriteSanitizeResult {
  // §NAMESPACED-GUARD-BYPASS — see isCustomerWriteGuarded. The guard must key on the SAME name the
  // authorization decision used, or a composite lane's customer write is authorized but unsanitized.
  const g: CustomerWriteGuard | undefined = CUSTOMER_WRITE_GUARDS[baseCollectionName(collection)];
  const out: Record<string, unknown> = { ...data };
  const stripped: string[] = [];
  // §EliteWeek-1C — universal pass first (all collections). A field the collection's own guard
  // manages explicitly (e.g. orders.paymentStatus with its createDefault) is skipped here so the
  // guard's richer semantics win below.
  const explicit = new Set((g?.ownerOnlyFields ?? []).map((f) => f.name));
  for (const name of UNIVERSAL_OWNER_ONLY_FIELDS) {
    if (explicit.has(name) || out[name] === undefined) continue;
    const prevVal = mode === 'update' && prev ? prev[name] : undefined;
    if (prevVal !== undefined) {
      if (out[name] !== prevVal) stripped.push(name);
      out[name] = prevVal; // pin to the stored (owner-set) value
    } else {
      stripped.push(name);
      delete out[name]; // strip — never store a client-forged owner field
    }
  }
  if (!g) return { data: out, stripped };
  for (const f of g.ownerOnlyFields) {
    if (mode === 'create') {
      if (f.createDefault !== undefined) {
        if (out[f.name] !== f.createDefault) stripped.push(f.name);
        out[f.name] = f.createDefault;
      } else if (out[f.name] !== undefined) {
        stripped.push(f.name);
        delete out[f.name];
      }
    } else {
      const prevVal = prev ? prev[f.name] : undefined;
      if (prevVal !== undefined) {
        if (out[f.name] !== prevVal) stripped.push(f.name);
        out[f.name] = prevVal;
      } else if (out[f.name] !== undefined) {
        stripped.push(f.name);
        delete out[f.name];
      }
    }
  }
  if (mode === 'update') {
    for (const name of g.keptAsCreated ?? []) {
      if (out[name] === undefined) continue;
      const kept = prev ? prev[name] : undefined;
      if (JSON.stringify(out[name]) !== JSON.stringify(kept)) stripped.push(name);
      if (kept !== undefined) out[name] = kept; else delete out[name];
    }
  }
  // §S7-STATUS-INITIAL — a customer create that names NO status starts at the collection's initial state.
  // The clamp below only ran when a status was sent, so the directory's own submission form (title,
  // category, area, contact) stored submissions with no status at all, although the contract requires one
  // and promises "customer create forces status→'pending'" (measured in production, Session 7).
  if (mode === 'create' && g.statusInitial !== undefined && out.status === undefined) out.status = g.statusInitial;
  // A customer may only set a status in customerStatuses. ANYTHING else is clamped to the safe
  // fallback — a forbidden STRING (e.g. 'completed'), OR a NON-STRING forgery (array/object/number).
  // The non-string case matters: without it a customer could store a malformed status that bypasses
  // the lifecycle + corrupts the owner's status display (e.g. status:['promoted_candidate']).
  if (g.customerStatuses && out.status !== undefined && !(typeof out.status === 'string' && g.customerStatuses.includes(out.status))) {
    const fallback =
      mode === 'create'
        ? g.statusInitial ?? undefined
        : prev && typeof prev.status === 'string'
          ? prev.status
          : g.statusInitial ?? undefined;
    if (out.status !== fallback) stripped.push(`status:${String(out.status)}->${String(fallback)}`);
    out.status = fallback;
  }
  // §BUG-TK2 — thread authorship. The customer can only author `customerAuthor` entries; a
  // non-customer ("support") entry is kept ONLY if it already existed in `prev` (owner-added via
  // the owner route). A forged support bubble is RELABELED to the customer author, so the display
  // can never show a customer-fabricated "Support said X" reply in their own isolated ticket.
  if (g.threadField && Array.isArray(out[g.threadField])) {
    const author = g.customerAuthor ?? 'you';
    const prevThread = prev && Array.isArray(prev[g.threadField]) ? (prev[g.threadField] as unknown[]) : [];
    const allowedNonCustomer = new Set(
      prevThread
        .filter((m): m is Record<string, unknown> => !!m && typeof m === 'object' && (m as Record<string, unknown>).who !== author)
        .map((m) => `${String(m.who)}\u0000${String(m.text ?? '')}`),
    );
    out[g.threadField] = (out[g.threadField] as unknown[]).map((m) => {
      if (!m || typeof m !== 'object') return m;
      const msg = m as Record<string, unknown>;
      if (msg.who === author) return m; // customer-authored entry — always fine
      const key = `${String(msg.who)}\u0000${String(msg.text ?? '')}`;
      if (allowedNonCustomer.has(key)) return m; // a genuine owner-added reply (was already there)
      stripped.push(`thread:who:${String(msg.who)}->${author}`);
      return { ...msg, who: author }; // forged "support" bubble → relabel as the customer
    });
  }
  return { data: out, stripped };
}
